/* Tektaş: shape tiles and the stone picker. The carat slider steps through the carats offered in the chosen shape
   and shows the stone on a hand at true size (on the two-column layout as a gallery slide, like Brilliant Earth's
   "true size" view, so the options column stays short; phones show it above the slider). The certificate (HRD or GIA)
   is its own step before the stones (owner 2026-10-04: each lab has its own prices), so the slider only offers the
   chosen lab's stones; under it the customer picks the colour and clarity when a carat has more than one. Those rows
   only check the real option chip ("1,00 ct · G · VS1 · GIA"), so assets/joy-product.js resolves the variant and the
   price.
   Loaded by snippets/joy-product-options.liquid only for products with "Kesim" and "Pırlanta" options. */
(() => {
  'use strict';
  // Per shape: [k, ratio, stone width ÷ photo width, stone height ÷ photo height]. Face-up width ≈ k × ∛ct mm and
  // length = ratio × width (round: the diameter), after Brilliant Earth's size chart (1 ct round 6.4 mm, oval
  // 8 × 5.5 mm, pear 8.5 × 5.5, marquise 10 × 5.3, emerald 6.5 × 5, heart 6.5, princess 5.5; heart and princess added
  // 2026-10-13 with İlkay's ring photos). The photos are Brilliant Earth's stone photos
  // (assets/joy-guide-<shape>.png); like on their loose-diamond pages, each is stretched to the stone's length and
  // width. Baget uses the emerald (step cut) photo.
  const SHAPES = {
    round: [6.5, 1, 0.502, 0.5], oval: [5.5, 1.45, 0.4975, 0.498], pear: [5.5, 1.55, 0.491, 0.504],
    marquise: [5.3, 1.9, 0.514, 0.498], emerald: [5, 1.3, 0.5, 0.504], heart: [6.5, 1, 0.494, 0.496],
    princess: [5.5, 1, 0.502, 0.5],
  };
  // Same scale as the Pırlanta Rehberi: the ring finger is ~19.65% of the hand photo's width, taken as 17.5 mm.
  const HAND_PERCENT_PER_MM = 19.65 / 17.5;
  const LABS = ['HRD', 'GIA', 'IGI'];
  // Colour and clarity from the lowest grade to the highest, so the chips read like the carat slider (adviser review
  // 2026-10-08: they came in feed order and the prices jumped). Lab-grown ranges ("D-F") rank by their first letter.
  const CLARITY = ['I3', 'I2', 'I1', 'SI3', 'SI2', 'SI1', 'VS2', 'VS1', 'VS', 'VVS2', 'VVS1', 'VVS', 'IF', 'FL'];
  const byGrade = (a, b) => {
    const rank = ([colour = '', clarity = '']) => [CLARITY.indexOf(clarity.toUpperCase()), -(colour.toUpperCase().charCodeAt(0) || 0)];
    const [clarityA, colourA] = rank(a);
    const [clarityB, colourB] = rank(b);
    return clarityA - clarityB || colourA - colourB;
  };

  // The lab logos' own sizes (assets/joy-hrd-logo.png, assets/joy-gia-logo.png).
  const LOGO_SIZE = { HRD: [420, 109], GIA: [360, 172] };
  const setChosen = (group) => {
    const label = group?.querySelector('[data-option-chosen]');
    const value = group?.querySelector('[data-value]:checked')?.dataset.value;
    if (label && value) label.textContent = value;
  };

  const setup = (group, number) => {
    const product = group.closest('joy-product') || document;
    const shapeGroup = product.querySelector('[data-shape-option]');
    let variants = [];
    try { variants = JSON.parse(product.querySelector('[data-stone-map]')?.textContent || '[]'); } catch (error) { /* every stone offered */ }
    const stoneIndex = Number(group.dataset.optionPosition) - 1;
    const lang = group.dataset.lang || document.documentElement.lang || 'tr';
    let images = {};
    try { images = JSON.parse(group.dataset.stones || '{}'); } catch (error) { /* no hand visual */ }
    const range = group.querySelector('[data-stone-range]');
    const ticks = group.querySelector('[data-stone-ticks]');
    const hand = group.querySelector('[data-stone-hand]');
    const stoneImage = group.querySelector('[data-stone-image]');
    const caption = group.querySelector('[data-stone-caption]');
    const visual = group.querySelector('[data-stone-visual]');
    const handSlide = product.querySelector('[data-hand-slide]');
    const handThumb = product.querySelector('[data-hand-thumb]');
    const caratText = group.querySelector('[data-stone-carat]');
    const mmText = group.querySelector('[data-stone-mm]');
    const qualityText = group.querySelector('[data-stone-quality-text]');
    const qualityRow = group.querySelector('[data-stone-quality]');
    const certRow = group.querySelector('[data-stone-cert]');
    const labRow = product.querySelector('[data-stone-lab]');
    const labChosen = labRow?.querySelector('[data-lab-chosen]');
    const chips = [...group.querySelectorAll('.stone-chip')].map((label) => {
      const input = label.querySelector('input');
      const [carat = '', ...rest] = input.dataset.value.split('·').map((part) => part.trim());
      const cert = LABS.includes(rest[rest.length - 1]?.toUpperCase()) ? rest.pop() : '';
      return { input, value: input.dataset.value, carat: parseFloat(carat.replace(',', '.')), quality: rest.join(' · '), grades: rest, cert };
    });
    let lab = chips.find((chip) => chip.input.checked)?.cert || '';
    // The lab the customer chose stays chosen: a shape without it shows the other lab, the next shape returns to it.
    let preferredLab = lab;
    // The chosen stone's option (1 Başlangıç, 2 Denge, 3 Prestij), kept when the group, shape or lab changes.
    let lastRole = 0;
    // Each stone's details from the weekly stone lists (product metafield joy.stones, tools/tas-listeleri/ligler.py),
    // by "Kesim|Pırlanta": g = its carat group on the 0,30 – 0,99 ct page, r = 1 Başlangıç, 2 Denge, 3 Prestij,
    // cut/pol/sym, fl = fluorescence, no = report number, url = the lab's own report check.
    let info = {};
    try { info = JSON.parse(group.querySelector('[data-stone-info]')?.textContent || '{}') || {}; } catch (error) { /* no details */ }
    const details = group.querySelector('[data-stone-details]');
    let labels = {};
    try { labels = JSON.parse(details?.dataset.labels || '{}'); } catch (error) { /* no labels */ }
    const leagues = product.querySelector('[data-stone-leagues]');
    const availability = group.querySelector('[data-stone-availability]');
    const soldNote = group.querySelector('[data-stone-sold]');
    const reportInput = group.querySelector('[data-stone-report]');
    let groupMode = false;
    // Values that do not start with a carat ("2,00 ct") keep the plain chips.
    if (!range || !chips.length || chips.some((chip) => !Number.isFinite(chip.carat))) {
      group.classList.add('is-plain');
      if (visual) visual.hidden = true;
      return;
    }
    const format = (value, digits) => value.toLocaleString(lang, { minimumFractionDigits: digits, maximumFractionDigits: digits });
    const shapeInput = () => shapeGroup?.querySelector('input:checked');
    let offered = chips;
    // The slider's steps: the carat groups where the stones have one (the 0,30 – 0,99 ct page shows three stones per
    // group), else every carat offered. chip.step is the stone's step for the chosen shape.
    let steps = [];
    const checked = () => offered.find((chip) => chip.input.checked);
    const infoOf = (chip) => info[`${shapeInput()?.dataset.value}|${chip.value}`];

    // Colour/clarity and certificate rows for the stones at this carat. Their inputs belong to no form
    // (form="joy-none"), so only the real option chip is sent with the order.
    const renderRow = (row, name, items, current, label) => {
      const list = row.querySelector('.chips');
      list.replaceChildren(...items.map((item) => {
        const chip = document.createElement('label');
        chip.className = 'chip';
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = `${name}-${number}`;
        input.value = item.value;
        input.setAttribute('form', 'joy-none');
        input.checked = item.value === current;
        const text = document.createElement('span');
        text.textContent = label(item);
        // A lab with its logo on the page (HRD Antwerp; GIA's retailer lock-up, owner 2026-10-13) shows the logo, named
        // by its alt text.
        const logo = row.dataset[`logo${item.value.charAt(0)}${item.value.slice(1).toLowerCase()}`];
        if (logo) {
          const image = document.createElement('img');
          image.className = `chip-logo is-${item.value.toLowerCase()}`;
          image.src = logo;
          image.alt = label(item);
          [image.width, image.height] = LOGO_SIZE[item.value] || [];
          text.replaceChildren(image);
        }
        chip.append(input, text);
        return chip;
      }));
    };
    // Where the Taş Listesi shows (every stone with its price) the colour/clarity chips would repeat it.
    const usesList = () => (groupMode
      ? offered.length > 0
      : (Object.keys(info).length > 0 && offered.length > 1)
        || (variants.length > 0 && steps.length >= 8 && offered.length <= steps.length * 1.5));
    const renderChoices = (step) => {
      const atCarat = offered.filter((chip) => chip.step === step);
      const current = checked() || atCarat[0];
      // Cheapest first for the chosen ayar, as in the Taş Listesi (adviser re-check 2026-10-08: colour often moves the
      // price more than clarity, so grade order made the prices jump); the grade decides between equal prices.
      const qualities = [...new Set(atCarat.map((chip) => chip.quality))]
        .map((quality) => {
          const same = atCarat.filter((chip) => chip.quality === quality);
          return { quality, grades: same[0].grades, price: Math.min(...same.map(priceOf)) };
        })
        .sort((a, b) => a.price - b.price || byGrade(a.grades, b.grades))
        .map((item) => item.quality);
      renderRow(qualityRow, 'stone-quality', qualities.map((value) => ({ value })), current?.quality, (item) => item.value);
      // A carat group lists its stones with their option names below instead.
      qualityRow.hidden = groupMode || qualities.length < 2 || (Boolean(listBox) && usesList());
      // One chip per lab: two stones of one grade (0,30 and 0,33 ct G SI1, both HRD) showed "HRD | HRD".
      const certs = [...new Set(atCarat.filter((chip) => chip.quality === current?.quality).map((chip) => chip.cert))]
        .map((value) => ({ value }))
        .sort((a, b) => LABS.indexOf(a.value) - LABS.indexOf(b.value));
      renderRow(certRow, 'stone-cert', certs, current?.cert, (item) => item.value);
      // One certificate (lab-grown: IGI) needs no choice: it is named under the size instead (not when the
      // Sertifika step above already names it).
      const single = certs.filter((item) => item.value).length === 1 && (!labRow || labRow.hidden);
      certRow.hidden = certs.filter((item) => item.value).length < 2;
      if (qualityText && current) {
        const [colour, clarity] = current.grades;
        qualityText.textContent = [
          colour && `${qualityText.dataset.colour} ${colour}`,
          clarity && `${qualityText.dataset.clarity} ${clarity}`,
          single && current.cert && `${qualityText.dataset.certificate} ${current.cert}`,
        ].filter(Boolean).join(' · ');
      }
    };
    // The step's readout and hand view: the chosen stone's own carat when it is in this step (a group's stones are
    // 0,30 – 0,35 ct), else the step's.
    const show = (step) => {
      const current = checked();
      const carat = current && current.step === step ? current.carat : step;
      const shape = shapeInput()?.dataset.shape || 'round';
      const [k, ratio, fillWidth, fillHeight] = SHAPES[shape] || SHAPES.round;
      const width = k * Math.cbrt(carat);
      caratText.textContent = `${format(carat, 2)} ct`;
      mmText.textContent = ratio === 1
        ? `≈ ${format(width, 1)} mm`
        : `≈ ${format(width * ratio, 1)} × ${format(width, 1)} mm`;
      if (caption) caption.textContent = `${caratText.textContent} · ${mmText.textContent}`;
      const index = steps.indexOf(step);
      range.style.setProperty('--fill', steps.length > 1 ? index / (steps.length - 1) : 0);
      range.setAttribute('aria-valuetext', `${format(carat, 2)} ct`);
      ticks.querySelectorAll('button').forEach((tick) => tick.classList.toggle('is-active', Number(tick.dataset.step) === index));
      thinTicks();
      const src = images[shape];
      hand.hidden = !src;
      if (src) {
        if (stoneImage.getAttribute('src') !== src) stoneImage.src = src;
        // Photo stretched so the stone inside it is width × length mm.
        stoneImage.style.width = `${(width * HAND_PERCENT_PER_MM) / fillWidth}%`;
        stoneImage.style.aspectRatio = `${fillHeight / (fillWidth * ratio)}`;
      }
    };
    // Carat labels under the slider: when they do not fit (the 3–5 ct page lists every stone, e.g. 3,00 3,01 3,02),
    // the chosen carat, the first and the last keep theirs, then every label that has room; the rest stay as ticks.
    const LABEL_PX = 40;
    let ticksWidth = 0;
    const thinTicks = () => {
      const buttons = [...ticks.querySelectorAll('button')];
      const count = buttons.length;
      const width = ticksWidth || ticks.clientWidth;
      if (!count || !width) return;
      const gap = Math.max(1, Math.ceil((LABEL_PX * (count - 1)) / width));
      const active = Number(range.value);
      // Whole carats first, then halves and tenths: 1,00 / 1,50 / 2,00 stay readable.
      const roundness = (index) => {
        const hundredths = Math.round(steps[index] * 100);
        if (hundredths % 100 === 0) return 0;
        if (hundredths % 50 === 0) return 1;
        return hundredths % 10 === 0 ? 2 : 3;
      };
      const byRoundness = buttons.map((tick, index) => index).sort((a, b) => roundness(a) - roundness(b) || a - b);
      const order = [active, 0, count - 1, ...byRoundness];
      const kept = [];
      order.forEach((index) => {
        if (!kept.includes(index) && kept.every((other) => Math.abs(other - index) >= gap)) kept.push(index);
      });
      buttons.forEach((tick, index) => tick.classList.toggle('is-minor', !kept.includes(index)));
    };
    if ('ResizeObserver' in window) {
      new ResizeObserver((entries) => {
        ticksWidth = entries[0].contentRect.width;
        thinTicks();
      }).observe(ticks);
    }
    // "Taş Listesi": where most carats are single stones (the 3–5 ct and 5 ct+ pages list every stone, often 0,01 ct
    // apart), every stone of the shape with its price for the chosen ayar, one tap each.
    const listBox = group.querySelector('[data-stone-list]');
    const listRows = group.querySelector('[data-stone-list-rows]');
    const moneyFormat = group.dataset.moneyFormat || '{{amount_with_comma_separator}}';
    const money = (cents) => {
      const [whole, fraction] = (cents / 100).toFixed(2).split('.');
      const grouped = (separator) => whole.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
      return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (match, kind) => {
        if (kind === 'amount_with_comma_separator') return `${grouped('.')},${fraction}`;
        if (kind === 'amount_no_decimals_with_comma_separator') return grouped('.');
        if (kind === 'amount_no_decimals') return grouped(',');
        return `${grouped(',')}.${fraction}`;
      });
    };
    const otherOptions = () => {
      const chosen = {};
      product.querySelectorAll('fieldset[data-option-position]').forEach((fieldset) => {
        if (fieldset === group || fieldset === shapeGroup) return;
        const value = fieldset.querySelector('input:checked')?.dataset.value;
        if (value) chosen[Number(fieldset.dataset.optionPosition) - 1] = value;
      });
      return chosen;
    };
    const entryFor = (chip) => {
      const shape = shapeInput()?.dataset.value;
      const shapeIndex = Number(shapeGroup?.dataset.optionPosition) - 1;
      const others = Object.entries(otherOptions());
      return variants.find((entry) => (!shape || entry.o[shapeIndex] === shape) && entry.o[stoneIndex] === chip.value
        && others.every(([index, value]) => entry.o[index] === value));
    };
    // A stone's price for the chosen ayar (from the stone map), for the chips' and the list's order.
    const priceOf = (chip) => entryFor(chip)?.p ?? Infinity;
    // A carat section (0,30 – 0,99 ct page) always lists its stones with their price, cheapest first (owner 2026-10-13:
    // 0,10 ct sections with many stones; an older list's Başlangıç, Denge, Prestij names still order it first).
    const renderList = () => {
      if (!listBox || !listRows) return;
      // Pages with weekly-list stones (1 ct and up) always list every stone; others when most carats are single stones.
      const useList = usesList();
      listBox.hidden = !useList;
      listBox.classList.toggle('is-groups', groupMode);
      if (!useList) return;
      const current = checked();
      const step = current?.step ?? steps[Number(range.value)];
      // Inside one carat the cheapest stone first, then by grade (the ayar chosen sets the prices).
      const rows = groupMode
        ? offered.filter((chip) => chip.step === step)
          .sort((a, b) => (infoOf(a)?.r || 9) - (infoOf(b)?.r || 9) || priceOf(a) - priceOf(b))
        : [...offered].sort((a, b) => a.carat - b.carat || priceOf(a) - priceOf(b) || byGrade(a.grades, b.grades)
          || LABS.indexOf(a.cert) - LABS.indexOf(b.cert));
      listRows.replaceChildren(...rows.map((chip) => {
        const entry = entryFor(chip);
        const role = groupMode ? labels[`r${infoOf(chip)?.r}`] : '';
        const row = document.createElement('button');
        row.type = 'button';
        row.className = 'stone-list-row';
        row.setAttribute('aria-pressed', String(chip === current));
        row.disabled = entry ? entry.a === false : false;
        const carat = document.createElement('strong');
        carat.textContent = role || `${format(chip.carat, 2)} ct`;
        const grade = document.createElement('span');
        // Lines break only between the carat, the grade and the report: "D · VS1" and "D-F" stay together.
        const keep = (text) => text.replace(/ /g, '\u00a0').replace(/-/g, '\u2011');
        grade.textContent = [role && keep(`${format(chip.carat, 2)} ct`), keep(chip.quality), chip.cert].filter(Boolean).join(' · ');
        const price = document.createElement('span');
        price.className = 'stone-list-price';
        price.textContent = entry && Number.isFinite(entry.p) ? money(entry.p) : '';
        row.append(carat, grade, price);
        row.addEventListener('click', () => chooseChip(chip));
        return row;
      }));
    };
    // Phones: the floating WhatsApp button steps aside while the stone list is on screen (joy-tektas.css); it covered
    // the prices on the right.
    if (listBox && 'IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        document.documentElement.classList.toggle('is-stone-list-in-view', entry.isIntersecting && !listBox.hidden);
      }).observe(listBox);
    }
    // Checks a stone at this carat, keeping the colour, clarity and certificate when possible.
    // Returns the input when the choice changed.
    // In a carat group the same option (role: 1 Başlangıç, 2 Denge, 3 Prestij) as before, else the group's first.
    const roleOf = (chip) => (chip && infoOf(chip)?.r) || 0;
    const sameRole = (atCarat, role) => {
      if (!groupMode) return null;
      const byRole = [...atCarat].sort((a, b) => (roleOf(a) || 9) - (roleOf(b) || 9) || priceOf(a) - priceOf(b));
      return byRole.find((chip) => role && roleOf(chip) === role) || byRole[0];
    };
    const pick = (step, previous) => {
      const current = checked();
      if (current && current.step === step) return null;
      const atCarat = offered.filter((chip) => chip.step === step);
      const next = sameRole(atCarat, lastRole)
        || atCarat.find((chip) => chip.quality === previous?.quality && chip.cert === previous?.cert)
        || atCarat.find((chip) => chip.cert === previous?.cert)
        || atCarat.find((chip) => chip.quality === previous?.quality)
        || atCarat[0];
      if (!next) return null;
      next.input.checked = true;
      return next.input;
    };
    // The stones offered with the chosen shape (every stone when the product has more variants than the map
    // lists); the slider has one step per carat.
    const refresh = () => {
      const shape = shapeInput()?.dataset.value;
      const shapeIndex = Number(shapeGroup?.dataset.optionPosition) - 1;
      const ofShape = shape && variants.length ? variants.filter((entry) => entry.o[shapeIndex] === shape) : null;
      const allowed = ofShape ? new Set(ofShape.map((entry) => entry.o[stoneIndex])) : null;
      // One stone line is the same physical stone(s) in 14 K and 18 K, each with the full stock: once either ayar is
      // sold out, the stone is gone, so the line leaves the picker (owner 2026-10-02: no stone sold twice).
      const sold = new Set((ofShape || []).filter((entry) => entry.a === false).map((entry) => entry.o[stoneIndex]));
      offered = allowed ? chips.filter((chip) => allowed.has(chip.value)) : chips;
      const unsold = offered.filter((chip) => !sold.has(chip.value));
      if (unsold.length) offered = unsold;
      // Sertifika step: shown when the shape has stones of two labs; the slider then offers the chosen lab's only.
      const labs = LABS.filter((value) => offered.some((chip) => chip.cert === value));
      if (labRow) {
        lab = labs.includes(preferredLab) ? preferredLab : labs.includes(lab) ? lab : labs[0] || '';
        labRow.hidden = labs.length < 2;
        renderRow(labRow, 'stone-lab', labs.map((value) => ({ value })), lab, (item) => item.value);
        if (labChosen) labChosen.textContent = lab;
        if (labs.length > 1) offered = offered.filter((chip) => chip.cert === lab);
      }
      leagues?.querySelectorAll('[data-lab]').forEach((price) => { price.hidden = price.dataset.lab !== lab; });
      groupMode = offered.some((chip) => infoOf(chip)?.g);
      offered.forEach((chip) => {
        const g = groupMode && infoOf(chip)?.g;
        chip.step = g ? parseFloat(String(g).replace(',', '.')) : chip.carat;
      });
      steps = [...new Set(offered.map((chip) => chip.step))].sort((a, b) => a - b);
      range.max = Math.max(steps.length - 1, 0);
      range.disabled = steps.length < 2;
      range.parentElement.style.setProperty('--steps', Math.max(steps.length - 1, 1));
      ticks.replaceChildren(...steps.map((step, index) => {
        const tick = document.createElement('button');
        tick.type = 'button';
        tick.tabIndex = -1;
        tick.dataset.step = index;
        tick.style.setProperty('--i', index);
        tick.textContent = format(step, 2);
        return tick;
      }));
    };
    // The chosen stone's details from the weekly lists: grades, fluorescence and its report, linked to the lab's
    // own report check (owner 2026-10-04).
    const renderDetails = () => {
      if (!details) return;
      const current = checked();
      const data = current && infoOf(current);
      if (availability) availability.hidden = !Object.keys(info).length;
      // The order records the exact stone the customer saw (adviser review 2026-10-04): "Rapor No: GIA 7451052300".
      if (reportInput) {
        reportInput.value = data?.no ? `${current.cert} ${data.no}` : '';
        reportInput.disabled = !reportInput.value;
      }
      details.hidden = !data;
      if (!data) return;
      const rows = [
        [data.cut ? labels.grades : labels.grades_fancy, [data.cut, data.pol, data.sym].filter(Boolean).join(' · ')],
        [labels.fl, labels[`fl_${data.fl}`] || data.fl],
      ];
      const logo = details.dataset[`logo${current.cert.charAt(0)}${current.cert.slice(1).toLowerCase()}`];
      if (data.no) rows.push([`${current.cert} ${labels.no}`, data.no, data.url, logo]);
      details.replaceChildren(...rows.map(([term, value, url, mark]) => {
        const item = document.createElement('div');
        const dt = document.createElement('dt');
        dt.textContent = term;
        if (mark) {
          const image = document.createElement('img');
          image.className = `details-logo is-${current.cert.toLowerCase()}`;
          image.src = mark;
          image.alt = '';
          [image.width, image.height] = LOGO_SIZE[current.cert] || [];
          dt.prepend(image);
        }
        const dd = document.createElement('dd');
        dd.textContent = value;
        if (url) {
          const link = document.createElement('a');
          link.href = url;
          link.target = '_blank';
          link.rel = 'noopener';
          link.textContent = labels.link;
          dd.append(' · ', link);
        }
        item.append(dt, dd);
        return item;
      }));
    };
    // Each ayar chip shows the chosen stone's price in that ayar ("14 K · 32.500 TL"): the ayar moves the price by about
    // a third, and on a phone neither the price above nor the sticky bar is in sight there (adviser review 2026-10-08).
    const ayarGroup = [...product.querySelectorAll('fieldset[data-option-position]')]
      .find((fieldset) => fieldset !== group && fieldset !== shapeGroup);
    const short = (cents) => money(cents).replace(/[.,]00(?!\d)/, '');
    const renderAyarPrices = () => {
      if (!ayarGroup || !variants.length) return;
      const current = checked();
      const shape = shapeInput()?.dataset.value;
      const shapeIndex = Number(shapeGroup?.dataset.optionPosition) - 1;
      const ayarIndex = Number(ayarGroup.dataset.optionPosition) - 1;
      ayarGroup.querySelectorAll('input[data-value]').forEach((input) => {
        const entry = current && variants.find((item) => (!shape || item.o[shapeIndex] === shape)
          && item.o[stoneIndex] === current.value && item.o[ayarIndex] === input.dataset.value);
        const label = input.nextElementSibling;
        let tag = label?.querySelector('.chip-price');
        if (!label || !entry || !Number.isFinite(entry.p)) { tag?.remove(); return; }
        if (!tag) {
          tag = document.createElement('small');
          tag.className = 'chip-price';
          label.append(tag);
        }
        tag.textContent = short(entry.p);
      });
    };
    const sync = () => {
      const step = checked()?.step ?? steps[0];
      range.value = Math.max(steps.indexOf(step), 0);
      show(step);
      renderChoices(step);
      setChosen(group);
      renderList();
      renderDetails();
      renderAyarPrices();
      if (groupMode && checked()) lastRole = roleOf(checked());
    };
    // The hand view moves to the gallery on the two-column layout, and back above the slider on phones.
    const wide = window.matchMedia('(min-width: 750px)');
    const placeHand = () => {
      if (!handSlide) return;
      if (wide.matches) handSlide.append(hand);
      else visual.prepend(hand);
      handSlide.hidden = !wide.matches;
      if (handThumb) handThumb.hidden = !wide.matches;
      product.refreshGallery?.();
    };
    const onHand = () => Boolean(handSlide) && product.slides?.[product.index] === handSlide;
    const showHand = () => {
      if (handSlide && !handSlide.hidden) product.showMedia?.('hand');
    };
    // A new stone also runs joy-product.js's photo match (showColour); a carat choice keeps the hand in view.
    const commit = (input, keepHand) => {
      if (input) input.dispatchEvent(new Event('change', { bubbles: true }));
      if (keepHand) showHand();
    };
    const chooseChip = (chip) => {
      if (chip === checked()) return;
      chip.input.checked = true;
      range.value = Math.max(steps.indexOf(chip.step), 0);
      show(chip.step);
      commit(chip.input, true);
    };
    const choose = (index) => {
      const step = steps[index];
      if (step === undefined) return;
      range.value = index;
      show(step);
      commit(pick(step, checked()), true);
    };

    refresh();
    // The page may open on a stone that is sold in the other ayar: move to the first stone still offered.
    if (!checked() && steps.length) {
      const opened = chips.some((chip) => chip.input.checked);
      const input = pick(steps[0]);
      if (opened && input) {
        commit(input, false);
        // Opened from a link to a stone that has sold: say so until the customer chooses.
        if (soldNote) {
          soldNote.hidden = false;
          product.addEventListener('pointerdown', () => { soldNote.hidden = true; }, { once: true });
        }
      }
    }
    group.classList.add('is-enhanced');
    visual.hidden = false;
    sync();
    placeHand();
    wide.addEventListener('change', placeHand);

    range.addEventListener('input', () => {
      show(steps[Number(range.value)]);
      showHand();
    });
    // The slider and the rows are not option inputs: their change stops here and the real chip they check
    // resolves the variant.
    range.addEventListener('change', (event) => {
      event.stopPropagation();
      choose(Number(range.value));
    });
    ticks.addEventListener('click', (event) => {
      const tick = event.target.closest('button');
      if (tick) choose(Number(tick.dataset.step));
    });
    [qualityRow, certRow].forEach((row) => row.addEventListener('change', (event) => {
      event.stopPropagation();
      const current = checked();
      if (!current) return;
      const quality = row === qualityRow ? event.target.value : current.quality;
      const cert = row === certRow ? event.target.value : current.cert;
      const atCarat = offered.filter((chip) => chip.step === current.step);
      const next = atCarat.find((chip) => chip.quality === quality && chip.cert === cert)
        || atCarat.find((chip) => chip.quality === quality);
      if (!next || next === current) return;
      next.input.checked = true;
      commit(next.input, onHand());
    }));
    group.addEventListener('change', (event) => {
      if (event.target.closest('.stone-chips')) sync();
    });
    // The list's prices follow the other options (ayar). Listen on the form: joy-product.js stops option changes there.
    (group.closest('form') || product).addEventListener('change', (event) => {
      if (group.contains(event.target) || shapeGroup?.contains(event.target)) return;
      setTimeout(() => {
        renderList();
        const step = checked()?.step;
        if (step !== undefined) renderChoices(step);
      });
    });
    // The offered stone nearest to the one before (its step taken before the refresh): the same step or the nearest
    // one, the same colour and clarity (and certificate) when there is one.
    const nearest = (before, beforeStep, beforeRole) => {
      if (!steps.length) return null;
      const target = before
        ? steps.reduce((best, step) => (Math.abs(step - beforeStep) < Math.abs(best - beforeStep) ? step : best), steps[0])
        : steps[0];
      const atCarat = offered.filter((chip) => chip.step === target);
      return sameRole(atCarat, beforeRole)
        || atCarat.find((chip) => chip.quality === before?.quality && chip.cert === before?.cert)
        || atCarat.find((chip) => chip.cert === before?.cert)
        || atCarat.find((chip) => chip.quality === before?.quality)
        || atCarat[0];
    };
    // A new shape keeps the carat (or the nearest one it has), the colour, clarity and certificate. This
    // listener sits on the shape fieldset, below joy-product.js's, so the variant is resolved with the new stone.
    shapeGroup?.addEventListener('change', () => {
      const before = checked();
      const beforeStep = before?.step;
      const beforeRole = lastRole;
      refresh();
      const next = nearest(before, beforeStep, beforeRole);
      if (next && (!checked() || checked().step !== next.step)) next.input.checked = true;
      sync();
      setChosen(shapeGroup);
    });
    // A new certificate: that lab's stone nearest to the one before. The step's inputs are not option inputs, so
    // their change stops here and the real chip resolves the variant.
    labRow?.addEventListener('change', (event) => {
      event.stopPropagation();
      const before = checked() || chips.find((chip) => chip.input.checked);
      const beforeStep = before?.step ?? before?.carat;
      const beforeRole = lastRole;
      lab = event.target.value;
      preferredLab = lab;
      refresh();
      const next = nearest(before, beforeStep, beforeRole);
      if (!next || next.input.checked) {
        sync();
        return;
      }
      next.input.checked = true;
      commit(next.input, onHand());
    });
  };

  // "Doğal | Lab Grown": the link opens the paired builder's stone in stock with the same shape and ayar and the
  // nearest carat. From lab-grown every natural builder is listed (one per carat range), so 2,00 ct opens the 2 ct
  // page, not 0,90 ct on the first one (adviser review 2026-10-08). Another shape only when none has the chosen one.
  const setupPair = (link) => {
    const product = link.closest('joy-product') || document;
    let pairs;
    try { pairs = JSON.parse(link.dataset.pair); } catch (error) { return; }
    if (!Array.isArray(pairs)) pairs = [pairs];
    const carat = (value) => parseFloat(String(value || '').split('·')[0].replace(',', '.'));
    const update = () => {
      const chosen = {};
      product.querySelectorAll('[data-option-name]').forEach((option) => {
        chosen[option.dataset.optionName] = option.querySelector('[data-value]:checked')?.dataset.value || '';
      });
      const stoneName = product.querySelector('[data-stone-option]')?.dataset.optionName;
      const wanted = carat(chosen[stoneName]);
      let best = null;
      pairs.forEach((pair) => pair.variants.forEach(([id, values]) => {
        let distance = 0;
        for (let index = 0; index < pair.names.length; index += 1) {
          const name = pair.names[index];
          if (name === stoneName) distance += Math.abs(carat(values[index]) - wanted) || 0;
          else if (chosen[name] !== undefined && chosen[name] !== values[index]) distance += 100;
        }
        if (!best || distance < best.distance) best = { url: pair.url, id, distance };
      }));
      if (best) link.href = `${best.url}?variant=${best.id}`;
    };
    update();
    product.addEventListener('change', update);
  };

  document.querySelectorAll('[data-stone-option]').forEach(setup);
  document.querySelectorAll('[data-pair-link]').forEach(setupPair);

  // Karat Ligleri and "Üst lige hazır mısınız?": the other league opens with the same shape and ayar on its smallest
  // stone in stock (adviser re-check 2026-10-08); a shape it does not have keeps the ayar. Without the data (offline,
  // an error) the plain link opens.
  document.addEventListener('click', async (event) => {
    const link = event.target.closest('a.stone-league:not(.is-current), a.stone-tier');
    if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const product = link.closest('joy-product') || document;
    const stoneName = product.querySelector('[data-stone-option]')?.dataset.optionName;
    const chosen = {};
    product.querySelectorAll('[data-option-name]').forEach((option) => {
      chosen[option.dataset.optionName] = option.querySelector('[data-value]:checked')?.dataset.value || '';
    });
    event.preventDefault();
    let target = link.href;
    try {
      const data = await fetch(`${link.pathname}.js`).then((response) => response.json());
      const names = data.options.map((option) => option.name || option);
      const carat = (value) => parseFloat(String(value || '').split('·')[0].replace(',', '.')) || 0;
      const lab = (value) => String(value || '').split('·').pop().trim();
      let best = null;
      data.variants.filter((variant) => variant.available).forEach((variant) => {
        let score = 0;
        names.forEach((name, index) => {
          if (name === stoneName) {
            // The smallest stone, with the report (HRD / GIA) the customer chose when there is one.
            score += carat(variant.options[index]);
            if (lab(chosen[name]) && lab(variant.options[index]) !== lab(chosen[name])) score += 50;
          } else if (chosen[name] && chosen[name] !== variant.options[index]) score += 1000;
        });
        if (!best || score < best.score || (score === best.score && variant.price < best.price)) best = { id: variant.id, price: variant.price, score };
      });
      if (best) target = `${link.pathname}?variant=${best.id}`;
    } catch (error) { /* the league's own page */ }
    window.location.href = target;
  });

  // The EX EX EX line's note for oval, pear and emerald (their reports give no cut grade): only with those shapes.
  const syncEx = () => document.querySelectorAll('[data-ex-fancy]').forEach((note) => {
    const product = note.closest('joy-product') || document;
    const shape = product.querySelector('[data-shape-option] input:checked')?.dataset.shape;
    const fancy = Boolean(shape) && shape !== 'round';
    note.hidden = !fancy;
    const badge = product.querySelector('[data-ex-badge]');
    if (badge) badge.textContent = fancy ? badge.dataset.fancy : badge.dataset.round;
  });
  document.addEventListener('change', syncEx);
  syncEx();

  // "Seçiminiz": every visible choice (heading → chosen value) and the price, right above the buttons. Rebuilt on
  // every change and whenever joy-product.js writes a new price.
  const setupSummary = (box) => {
    const product = box.closest('joy-product') || document;
    const form = box.closest('form');
    const list = box.querySelector('[data-tektas-summary-list]');
    const priceOut = box.querySelector('[data-tektas-summary-price]');
    if (!form || !list) return;
    const read = (group) => {
      const heading = group.querySelector(':scope > .option-heading legend, :scope > .option-heading label');
      if (!heading) return null;
      const name = [...heading.childNodes].filter((node) => node.nodeType === Node.TEXT_NODE)
        .map((node) => node.textContent).join('').trim();
      const checked = group.querySelector('input[data-value]:checked') || group.querySelector('input:checked');
      const select = group.querySelector('select');
      const value = checked ? (checked.dataset.value || checked.value) : select?.selectedOptions[0]?.text || '';
      return name && value.trim() ? [name, value.trim()] : null;
    };
    const render = () => {
      const rows = [...form.querySelectorAll('.option-group')]
        .filter((group) => !group.classList.contains('stone-choice') && !group.closest('[hidden]'))
        .map(read).filter(Boolean);
      // The free engraving, as its summary under "Kişiselleştirin" shows it.
      const engraving = form.querySelector('[data-engrave-text-input]');
      if (engraving && !engraving.disabled && engraving.value) {
        const label = form.querySelector('[data-engrave-summary] strong')?.textContent.replace(/:\s*$/, '').trim();
        const text = form.querySelector('[data-engrave-summary-text]')?.textContent.trim();
        if (label && text) rows.push([label, text]);
      }
      list.replaceChildren(...rows.flatMap(([name, value]) => {
        const term = document.createElement('dt');
        term.textContent = name;
        const detail = document.createElement('dd');
        detail.textContent = value;
        return [term, detail];
      }));
      const mainPrice = product.querySelector('[data-main-price]');
      if (priceOut && mainPrice) priceOut.textContent = mainPrice.textContent.trim();
      box.hidden = !rows.length;
    };
    // After every change handler (joy-product.js shows or hides the Model row), also in a background tab.
    form.addEventListener('change', () => setTimeout(render));
    form.addEventListener('joy:engraving', render);
    // joy-product.js replaces the whole price block when the new variant arrives: watch its parent's children
    // (not the subtree, which holds this summary too).
    const priceHolder = product.querySelector('[data-product-price]')?.parentElement;
    if (priceHolder) new MutationObserver(render).observe(priceHolder, { childList: true });
    render();
  };
  document.querySelectorAll('[data-tektas-summary]').forEach(setupSummary);

  // "Daha uygun bir taş mı buldunuz?": the link opens the stone request panel under the product details; after
  // sending, Shopify returns to the form's anchor, so that panel opens too (with the thank-you note).
  const openRequest = (hash) => {
    const target = hash && hash.length > 1 ? document.getElementById(decodeURIComponent(hash.slice(1))) : null;
    const details = target?.closest('details.stone-request');
    if (details) details.open = true;
  };
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-open-request]');
    if (link) openRequest(link.hash);
  });
  openRequest(window.location.hash);
})();
